'use client'

import React, { useEffect } from 'react'

import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { Button, ButtonGroup, styled } from '@mui/material'

import { useTranslations } from '@/hooks/useTranslations'
import { fontNames } from '@/themes/commerce/fonts'
import { commerceTheme as theme } from '@/themes/commerce/theme'
import { ValueTextField } from './QuantityCounter.styled'

import type { FC } from 'react'

interface QuantityCounterProps {
  value: number
  updateCounter: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean
  tabIndex?: number
  inputClassName?: string
}

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '2px',
  borderWidth: '2px',
  minWidth: 'unset',

  '&:hover': {
    borderWidth: '2px',
  },

  '&:hover *': {
    color: theme.palette.text.light,
  },
}))

const DecreaseButton = styled(StyledButton)(({ theme }) => ({
  borderColor: theme.palette.secondary.main,

  '&:hover': {
    borderColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.secondary.main,
  },
}))

const IncreaseButton = styled(StyledButton)(({ theme }) => ({
  borderColor: theme.palette.primary.main,

  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}))

export const QuantityCounter: FC<QuantityCounterProps> = ({
  value,
  updateCounter,
  min = 0,
  max = 999,
  disabled = false,
  tabIndex = 1,
  inputClassName,
}) => {
  const disabledMin = value <= min || disabled
  const disabledMax = value >= max || disabled
  const disabledMaxTextField = value >= max + 1 || disabled
  const inputRef = React.useRef<HTMLInputElement>(null)
  // const debouncedValue = useDebounce(value, 500)
  const { translate } = useTranslations()

  // FIXME: [NE-4812] Debounce is not working properly
  // useEffect(() => {
  //   if (max && debouncedValue > max) {
  //     updateCounter(max)
  //   }
  // }, [debouncedValue, max, min, updateCounter])

  useEffect(() => {
    const element = inputRef.current
    if (!element) {
      return
    }

    const handleOnScroll = (event: WheelEvent) => {
      event.preventDefault()
      event.stopPropagation()
    }

    element.addEventListener('wheel', handleOnScroll, { passive: false })

    return () => {
      element.removeEventListener('wheel', handleOnScroll)
    }
  }, [])

  return (
    <ButtonGroup
      variant="base"
      sx={{
        flex: 3,
        alignItems: 'center',
        backgroundColor: theme.palette.background.pale,
      }}
    >
      <DecreaseButton
        disabled={disabledMin}
        title={translate('quantity_counter.button.decrease')?.toString()}
        aria-label={translate('quantity_counter.button.decrease')?.toString()}
        onClick={() => updateCounter(value - 1)}
        size="small"
        variant="outlined"
      >
        <RemoveIcon color={disabledMin ? 'disabled' : 'secondary'} />
      </DecreaseButton>
      <ValueTextField
        ref={inputRef}
        className={inputClassName}
        variant="standard"
        value={value}
        fullWidth
        InputProps={{
          inputProps: {
            min,
            max,
            tabIndex,
            style: {
              color: theme.palette.text.primary,
              fontFamily: fontNames.nunito,
              fontSize: '16px',
              fontWeight: 'bold',
              height: 'inherit',
              padding: 0,
            },
          },
        }}
        disabled={disabledMaxTextField}
        type="number"
        onChange={(event) => updateCounter(Number(event.target.value))}
        onFocus={(event) => event.target.select()}
        sx={{ flex: 3 }}
      />
      <IncreaseButton
        title={translate('quantity_counter.button.increase')?.toString()}
        aria-label={translate('quantity_counter.button.increase')?.toString()}
        disabled={disabledMax}
        onClick={() => updateCounter(value + 1)}
        size="small"
        variant="outlined"
      >
        <AddIcon color={disabledMax ? 'disabled' : 'primary'} />
      </IncreaseButton>
    </ButtonGroup>
  )
}
